import { Box } from "../../box";
import { forwardRef, useMemo } from "react";
import { isNil, mergeProps, omitProps, resolveChildren, useChainedEventCallback, useCheckableProps, useSlots } from "../../shared";
import { useResponsiveValue } from "../../styling";
import { Text } from "../../typography";
import { VisuallyHidden } from "../../visually-hidden";
import { embeddedIconSize } from "../../icons";
import { useCheckbox } from "./useCheckbox";
import { useFieldInputProps } from "../../field";
import { useToolbarProps } from "../../toolbar";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DefaultElement = "label";
export function InnerCheckbox(props) {
  const [checkableProps] = useCheckableProps(props);
  const [fieldProps, isInField] = useFieldInputProps();
  const [toolbarProps] = useToolbarProps();
  const {
    active,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    as = DefaultElement,
    autoFocus,
    checked,
    children,
    defaultChecked,
    defaultIndeterminate,
    disabled,
    focus,
    forwardedRef,
    hover,
    id,
    indeterminate,
    name,
    onChange,
    onCheck,
    onValueChange,
    required,
    reverse,
    // Usually provided by the field inputs.
    size,
    tabIndex,
    validationState,
    value,
    ...rest
  } = mergeProps(omitProps(props, ["role"]), checkableProps, omitProps(toolbarProps, ["orientation"]), omitProps(fieldProps, ["fluid"]));

  if (isNil(children) && isNil(ariaLabel) && isNil(ariaLabelledBy)) {
    console.error("A checkbox must either have children, an \"aria-label\" attribute or an \"aria-labelledby\" attribute.");
  }

  const sizeValue = useResponsiveValue(size);
  const handleChange = useChainedEventCallback(onChange, (event, isChecked) => {
    if (!isNil(onValueChange)) {
      onValueChange(event, isChecked);
    }

    if (!isNil(onCheck)) {
      onCheck(event, value);
    }
  });
  const {
    inputProps,
    wrapperProps
  } = useCheckbox({
    active,
    ariaLabel,
    ariaLabelledBy,
    autoFocus,
    checked,
    cssModule: "o-ui-checkbox",
    defaultChecked,
    defaultIndeterminate,
    disabled,
    focus,
    forwardedRef,
    hover,
    id,
    indeterminate,
    isInField,
    name,
    onChange: handleChange,
    required,
    reverse,
    size: sizeValue,
    tabIndex,
    validationState
  });
  const content = resolveChildren(children);
  const {
    counter,
    icon,
    text
  } = useSlots(content, useMemo(() => ({
    _: {
      defaultWrapper: Text
    },
    counter: {
      className: "o-ui-checkbox-counter",
      color: "inherit",
      pushed: true,
      reverse,
      size: sizeValue,
      variant: "divider"
    },
    icon: {
      className: "o-ui-checkbox-icon",
      size: embeddedIconSize(sizeValue)
    },
    text: {
      className: "o-ui-checkbox-label",
      color: "inherit",
      size: sizeValue
    }
  }), [sizeValue, reverse]));
  return /*#__PURE__*/_jsxs(Box, { ...mergeProps(rest, {
      as
    }, wrapperProps),
    children: [/*#__PURE__*/_jsx(VisuallyHidden, { ...inputProps
    }), /*#__PURE__*/_jsx("span", {
      className: "o-ui-checkbox-box"
    }), text, icon, counter]
  });
}
InnerCheckbox.defaultElement = DefaultElement;
export const Checkbox = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/_jsx(InnerCheckbox, { ...props,
  forwardedRef: ref
}));